import { useI18n } from '../i18n';

interface Tool {
    name: string;
    description: string;
    url: string;
}

interface StructuredDataProps {
    tools?: Tool[];
}

export function StructuredData({ tools = [] }: StructuredDataProps) {
    const { t, locale } = useI18n();

    const websiteSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'GetDev.Tools',
        alternateName: 'Developer Tools Collection',
        url: window.location.origin,
        description: t.seo.siteDescription,
        inLanguage: locale,
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${window.location.origin}/?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
        },
    };

    const organizationSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'GetDev.Tools',
        url: window.location.origin,
        logo: `${window.location.origin}/logo.png`,
        sameAs: [],
    };

    const softwareApplicationSchema = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'GetDev.Tools',
        applicationCategory: 'DeveloperApplication',
        operatingSystem: 'Web Browser',
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
        },
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '5',
            ratingCount: '1',
        },
    };

    const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: window.location.origin,
            },
        ],
    };

    type SchemaType = typeof websiteSchema | typeof organizationSchema | typeof softwareApplicationSchema | typeof breadcrumbSchema;
    const schemas: SchemaType[] = [
        websiteSchema,
        organizationSchema,
        softwareApplicationSchema,
        breadcrumbSchema,
    ];

    // Tools list schema (separate from base schemas)
    const toolsSchema = tools.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: tools.map((tool, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
                '@type': 'SoftwareApplication',
                name: tool.name,
                description: tool.description,
                url: tool.url,
                applicationCategory: 'DeveloperApplication',
                operatingSystem: 'Web Browser',
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
            },
        })),
    } : null;

    return (
        <>
            {schemas.map((schema, index) => (
                <script
                    key={index}
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
                />
            ))}
            {toolsSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(toolsSchema) }}
                />
            )}
        </>
    );
}
